<template>
  <div class="challenge-card relative cursor-pointer" :class="{'with-upgrade-overlay-cta': showChallengeUpgradeCTA, '!cursor-default': disabled && !showChallengeUpgradeCTA}"
       @click="visitChallenge('challengeCard.titleClickDetail')">
    <div v-if="showChallengeUpgradeCTA" class="absolute w-full h-full z-50 backdrop-blur-sm rounded justify-center text-center align-middle tw-flex flex-col">
      <icon class="lock-icon mx-auto" name="lock"
            size="large"></icon>
      <div class="challenge-cta__overlay">
        <h3 class="h3 w-3/4 m-auto">
          <router-link to="/profile/subscription#upgrade">{{
              splitCtaText[1]
            }}
          </router-link>
        </h3>
      </div>
    </div>
    <div class="challenge">
      <div class="challenge-image"
           :style="{'background-image': `url('${challengeImageUrl}')`}"
           @error="handleImageError">
        <div class="challenge-unseen-badge" v-if="isNew">{{ $t('new_generic') }}</div>
        <div class="challenge-image-tags">
          <DsButton
            v-if="isLoggedIn && canUserAccessChallengeDetail && !disabled"
            :variant="challengeCopy.isFollowing ? 'rounded-secondary' : 'rounded'"
            size="extra-small"
            :label="challengeCopy.isFollowing ? 'Following' : 'Follow'"
            @click.stop="togglePinStatus"
            :loading="togglingPinStatus"
          />
        </div>
        <div class="challenge-image-actor"
             v-if="challenge && challenge.actor && challenge.actor.url && challenge.id > 0 && !challenge.isAnonymous">
          <ActorAvatar :actor="challenge.actor" :actor-logo-url="challenge.actor.url" :width="75"/>
        </div>
      </div>
      <div class="challenge-content-container"
           :class="canUserAccessChallengeDetail ? '' : 'challenge-text-without-discover-button'">
        <h3 class="h3" :class="{clickable: canUserAccessChallengeDetail}">{{ challenge.title }}</h3>

        <p class="challenge-description"
           v-html="localizedDisplayProperty(challenge, 'description')">
        </p>

        <div class="challenge-tags">
          <span v-if="challenge.status != 'new'">{{ getLabelForChallengeStatus(challenge.status) }}</span>
          <span v-else-if="challenge.status === 'new' && daysSinceCreation <= 5">{{
              getLabelForChallengeStatus(challenge.status)
            }}</span>
          <span :class="visibilityLabel.toLowerCase()">{{ visibilityLabel.toUpperCase() }}</span>
        </div>

        <div class="challenge-bottom-container">
          <div style="display: flex; flex-direction: row; margin-top: 0.5rem;"
               v-if="challengeGoals && challengeGoals.length > 0">
            <Icon name="hand-shake"/>
            <p style="margin-left: 0.5rem; font-size: 14px;" class="challenge-goals"
               v-tooltip.top="challengeGoals.length > 3 ? challengeGoals.join(', ') : ''">
              {{
                challengeGoals.length > 3 ? challengeGoals.slice(0, 3).join(', ') + '...' : challengeGoals.slice(0, 3).join(', ')
              }}</p>
          </div>

          <div style="display: flex; margin-top: 0.5rem;" v-if="challenge.endDate">
            <Icon name="clock" style="margin-right: 0.5rem;"/>
            <p v-html="getChallengeTimeDifference(challenge.endDate, true)"></p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Tag from '../Tag/Tag.vue'
  import { trackHeapEvent } from '../../util/analytics.js'
  import { getDefaultFallbackImageUrl, getLabelForChallengeStatus, splitCtaText } from '../../util/helpers.js'
  import { getChallengeTimeDifference } from '../../util/date.js'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import Icon from '@/components/Icon/Icon.vue'
  import { getLogoDevLogoUrl } from '@/util/actor'
  import { fetchChallenge, updatePinStatus } from '@/Domain/Challenge/Api/challenges'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import ChallengeMixin from '@/util/ChallengeMixin'

  export default {
    name: 'ChallengeCardNew',
    mixins: [TranslationsMixin, ChallengeMixin],
    components: {
      ActorAvatar,
      Icon,
      Tag,
    },
    props: {
      challenge: {
        type: Object,
        required: true
      },
      isVertical: Boolean,
      isContainer: {
        type: Boolean,
        default: true,
      },
      canToggleStatusFilter: Boolean,
      canToggleVisibilityFilter: Boolean,
      disabled: Boolean,
      isNew: Boolean,
    },
    data() {
      return {
        countdownKey: 0,
        endTimeInterval: null,
        fixedImageSize: 0,
        challengeCopy: {},
        togglingPinStatus: false,
        challengeImageUrl: '',
        hasImageError: false
      }
    },
    computed: {
      challengeGoals() {
        if (this.challengeCopy && this.challengeCopy.title) {
          const combined = [
            ...(this.challengeCopy.activities || []).map(item => ({
              ...item,
              label: this.getLabelForTaxonomyValue('activities', item.value || item.id) || item.label,
              type: 'activities'
            })),
            ...(this.challengeCopy.industries || []).map(item => ({
              ...item,
              label: this.getLabelForTaxonomyValue('industries', item.value || item.id) || item.label,
              type: 'industries'
            })),
            ...(this.challengeCopy.technology || []).map(item => ({
              ...item,
              label: this.getLabelForTaxonomyValue('technologies', item.value || item.id) || item.label,
              type: 'technologies'
            })),
            ...(this.challengeCopy.domains || []).map(item => ({
              ...item,
              label: this.getLabelForTaxonomyValue('domains', item.value || item.id) || item.label,
              type: 'domains'
            })),
            ...(this.challengeCopy.stages || []).map(item => ({
              ...item,
              label: this.getLabelForTaxonomyValue('stages', item.value || item.id) || item.label,
              type: 'stages'
            })),
          ]
          return combined.map(item => item.label)
        }

        return []
      },
      communityId() {
        return this.$route.params.communityId
      },
      challengeCtaUpgradeText() {
        if (this.showChallengeUpgradeCTA) {
          return this.config.packageCTAs.challenge.text || this.$t('default_challenge_upgrade_cta', { challenge: this.challengeLabel })
        }
        return ''
      },
      splitCtaText() {
        return splitCtaText(this.challengeCtaUpgradeText)
      },
      showChallengeUpgradeCTA() {
        return this.isLoggedIn && !this.canUserAccessChallengeDetail && this.config.packageCTAs && this.config.packageCTAs.challenge && this.config.packageCTAs.challenge.enabled
      },
      config() {
        return this.$store.state.config
      },
      visibilityValue() {
        return this.challenge.invite_only === 1 ? 'private' : 'public'
      },
      visibilityLabel() {
        return this.challenge.visibility === 'private' ? this.$t('challenge_private') : this.$t('challenge_public')
      },
      isLoggedIn() {
        return this.$store.getters.isLoggedIn
      },
      daysSinceCreation(): number {
        const createdDate = new Date(this.challenge.createdAt)
        const now = new Date()
        const timeDiff = now.getTime() - createdDate.getTime()
        return Math.floor(timeDiff / (1000 * 3600 * 24))
      },
    },
    methods: {
      getClearbitActorLogo: getLogoDevLogoUrl,
      trackHeapEvent,
      getChallengeTimeDifference,
      getLabelForChallengeStatus,
      calculateImageHeight() {
        if (window.innerWidth < 992) {
          if (this.isVertical && this.$refs.challengeContainer) {
            this.fixedImageSize = `calc(0.28125 * ${getComputedStyle(this.$refs.challengeContainer).width})`
          } else {
            this.fixedImageSize = 'auto'
          }
          return
        }
        if (this.$refs.challengeContainer) {
          // aspect ratio 0.5625 == 16:9, divided by 2 or 4 because image is half of total width
          if (this.isVertical) {
            this.fixedImageSize = `calc(0.28125 * ${getComputedStyle(this.$refs.challengeContainer).width})`
          } else {
            this.fixedImageSize = `calc(0.140625 * ${getComputedStyle(this.$refs.challengeContainer).width})`
          }
        }
      },
      visitChallenge(eventName) {
        if (this.disabled) {
          return
        }

        trackHeapEvent(eventName)
        if (this.canUserAccessChallengeDetail) {
          if (this.communityId) {
            this.$router.push(`/communities/` + this.communityId + '/challenges/' + this.challenge.id)
            return
          }
          this.$router.push('/challenges/' + this.challenge.id)
        }
      },
      onClickVisibilityTag() {
        if (this.canToggleVisibilityFilter) {
          this.$emit('set-filter', { target: 'visibility', value: this.visibilityValue })
        }
      },
      onClickStatusTag() {
        if (this.canToggleStatusFilter) {
          this.$emit('set-filter', { target: 'status', value: this.challenge.status })
        }
      },
      fetchChallengeData() {
        fetchChallenge(this.challenge.id)
          .then((response) => {
            this.challengeCopy = response
          })
      },
      togglePinStatus() {
        this.togglingPinStatus = true
        updatePinStatus(this.challenge.id)
          .then(() => {
            this.fetchChallengeData()
          })
          .finally(() => {
            this.togglingPinStatus = false
          })
      },
      handleImageError() {
        this.hasImageError = true
        this.challengeImageUrl = getDefaultFallbackImageUrl()
      }
    },
    mounted() {
      this.endTimeInterval = setInterval(() => {
        this.countdownKey++
      }, 1000)

      if (this.challenge) {
        this.challengeCopy = this.challenge

        if (this.challengeCopy.imageURL) {
          this.challengeImageUrl = this.challengeCopy.imageURL
        } else {
          this.challengeImageUrl = getDefaultFallbackImageUrl()
        }
      }
    },
    beforeUnmount() {
      if (this.endTimeInterval) {
        clearInterval(this.endTimeInterval)
      }
      this.$bus.off('resize')
    },
    watch: {
      challenge: {
        immediate: true,
        handler(newChallenge) {
          if (newChallenge) {
            this.challengeCopy = { ...newChallenge }

            if (!this.challengeCopy.image) {
              this.challengeCopy.image = getDefaultFallbackImageUrl()
            }
          }
        }
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "../../../scss/variables";

  .challenge-card {
    height: 450px;
    border: 1px solid lightgrey;
    border-radius: 0.5em;
    box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);
    background-size: cover;
    background: white no-repeat center;

    .has-default-image {
      background-size: contain;
    }

    &:hover {
      border: 1px solid var(--primary-community);
      box-shadow: 0px 0px 5px 1px rgba(176, 176, 176, 0.5);
    }
  }

  .challenge-content-container {
    padding: 1rem;
  }

  .challenge-description {
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 1rem;
    height: 60px;
  }

  .challenge-image {
    border-radius: 0.4rem 0.4rem 0 0;
    height: 180px;
    width: 100%;
    position: relative;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  .challenge-image-tags {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  .challenge-unseen-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1;
    background: var(--primary-community);
    color: white;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
  }

  .challenge-image-actor {
    width: 75px;
    height: 75px;
    position: absolute;
    right: 15px;
    bottom: -10px;
  }

  .challenge-tags {
    height: 30px;
    display: flex;
    flex-direction: row;

    span {
      border: 1px solid black;
      padding: 0px 1rem;
      margin-right: 0.5rem;
      align-content: center;
      height: 25px;
    }

    .public {
      color: var(--primary-community);
      border: 1px solid var(--primary-community);
    }

    .private {
      color: black;
      border: 1px solid black;
    }
  }

  .challenge-bottom-container {
    .challenge-goals {
      word-break: break-word;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
</style>
